import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Building2, ArrowRight, Video, Users, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/** /academy — Academy organization signup: workspace + $1,495 annual membership. */
export default function AcademySignup() {
  const nav = useNavigate();
  const { setUser, fetchTeam } = useAuth();
  const [f, setF] = useState({ academy_name: "", contact_name: "", email: "", password: "", phone: "", sport: "", city: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (ev) => {
    ev.preventDefault();
    if (f.password.length < 8) return toast.error("Password must be at least 8 characters");
    setBusy(true);
    try {
      await api.post("/academy/signup", f);
      const { data: me } = await api.get("/auth/me");
      setUser(me);
      await fetchTeam();
      toast.success("Academy workspace created!");
      const { data } = await api.post("/academy/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (er) { toast.error(formatApiError(er)); setBusy(false); }
  };

  const cls = "bg-white/5 border-white/15 text-white placeholder:text-white/35";
  return (
    <div className="min-h-screen" style={{ background: "radial-gradient(1100px 480px at 50% -120px, #0B1E3666, transparent), var(--ink-1)" }}>
      <div className="max-w-5xl mx-auto px-5 pb-24 grid lg:grid-cols-2 gap-12">
        <div className="pt-16">
          <div className="text-[10px] uppercase tracking-[0.3em] font-black text-amber-400 flex items-center gap-2">
            <Building2 className="h-3.5 w-3.5" /> BioHacker Academy™
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-white mt-3 leading-tight">Run your academy on the Athlete Performance OS</h1>
          <p className="text-sm text-white/55 mt-4 max-w-md">
            Roster management, athlete intelligence, and <span className="text-white font-bold">BioHacker Live Coach™</span> — real-time visual coaching between your staff and athletes, anywhere.
          </p>
          <div className="mt-8 space-y-4">
            {[
              { icon: DollarSign, t: "$1,495 / year Academy Membership", d: "One annual platform fee for your whole organization." },
              { icon: Users, t: "Pay-as-you-grow player billing", d: "Simple monthly revenue share per active academy player — you set your coaching prices." },
              { icon: Video, t: "Live Coach included", d: "Two-way booking + private live video training rooms for your coaches and athletes." },
            ].map(({ icon: I, t, d }) => (
              <div key={t} className="flex gap-3">
                <div className="h-9 w-9 rounded-xl bg-amber-400/10 border border-amber-400/25 flex items-center justify-center shrink-0"><I className="h-4 w-4 text-amber-300" /></div>
                <div><div className="text-sm font-bold text-white">{t}</div><div className="text-xs text-white/50 mt-0.5">{d}</div></div>
              </div>
            ))}
          </div>
        </div>
        <form onSubmit={submit} className="lg:pt-16" data-testid="academy-signup-form">
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6 space-y-3">
            <h2 className="text-lg font-black text-white">Create your Academy workspace</h2>
            <Input placeholder="Academy name *" required value={f.academy_name} onChange={set("academy_name")} className={cls} data-testid="academy-name-input" />
            <Input placeholder="Your name *" required value={f.contact_name} onChange={set("contact_name")} className={cls} data-testid="academy-contact-input" />
            <Input type="email" placeholder="Email *" required value={f.email} onChange={set("email")} className={cls} data-testid="academy-email-input" />
            <Input type="password" placeholder="Password (8+ characters) *" required value={f.password} onChange={set("password")} className={cls} data-testid="academy-password-input" />
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Primary sport" value={f.sport} onChange={set("sport")} className={cls} data-testid="academy-sport-input" />
              <Input placeholder="Phone" value={f.phone} onChange={set("phone")} className={cls} data-testid="academy-phone-input" />
            </div>
            <Input placeholder="City / location" value={f.city} onChange={set("city")} className={cls} data-testid="academy-city-input" />
            <Button type="submit" disabled={busy} className="w-full font-black py-6 bg-amber-400 hover:bg-amber-300 text-[#0b1424]" data-testid="academy-submit-btn">
              {busy ? "Creating workspace…" : "Create workspace & pay $1,495/yr"} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <div className="text-[11px] text-white/40 text-center">
              Secure checkout via Stripe. Already have an account? <Link to="/login" className="underline text-white/60">Sign in</Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
